import React, { useEffect, useState } from 'react';
import Button from '../../../shared/components/ui/Button';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getAllLands,
  handleCheckAccomodationDelete,
  handleCheckBlockDelete,
  handleCheckCategoryDelete,
  handleCheckCompanyDelete,
  handleCheckDocumentDelete,
  handleCheckExtraChargeDelete,
  handleCheckLandDelete,
  handleCheckLevelDelete,
  handleCheckPaymentStageDelete,
  handleCheckPLCDelete,
  handleCheckProjectDelete,
  handleCheckQuotationDelete,
  handleCheckRequirementDelete,
  handleCheckServiceProviderDelete,
  handleCheckTemplateDelete,
  handleCheckUnitDelete,
  handleCheckUserDelete,
  handleCheckVendorDelete,
  checkLiveTaskDelete,
  handleCheckLeadDelete,
  handleCheckClosedDealsDelete,
  handleCheckUCDelete,
  handleCheckInvoiceDelete,
  handleCheckMachineDelete,
  handleCheckWorkTypeDelete,
  handleCheckIndepdendentPaymentStageDelete
} from '../../services/api/implementation/impl';
import toast from 'react-hot-toast';
import Loader from '../../../shared/components/ui/Loader';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const DeleteConfirmationModal = ({
  name,
  onClose,
  title,
  data,
  type,
  onDeleteClick,
  setCanDelete,
  setSelectedIds,
  selectedIds,
  isNotBulk = true
}) => {
  const userId = Cookies.get('userId');
  const [isBulk, setIsBulk] = useState(false);
  const [arrayData, setArrayData] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  // Track page view
  useEffect(() => {
    mixpanel.track('Delete Confirmation Modal Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (isBulk) {
      setArrayData(data);
    } else {
      setArrayData([]);
    }
  }, [isBulk]);

  const createDeleteMutation = (deleteFn) =>
    useMutation({
      mutationFn: deleteFn,
      onSuccess: async (res, id, variables) => {
        if (res.canDelete) {
          setSelectedItems((prev) => [...prev, id]);
        }
      },
      onError: (err) => {
        console.error('Error checking dependencies', err);
        toast.error(err?.response?.data?.message || err?.message || 'Error checking dependencies');
      }
    });

  // Map of type to mutation
  const deleteMutations = {
    land: createDeleteMutation(handleCheckLandDelete),
    project: createDeleteMutation(handleCheckProjectDelete),
    accommodation: createDeleteMutation(handleCheckAccomodationDelete),
    block: createDeleteMutation(handleCheckBlockDelete),
    unit: createDeleteMutation(handleCheckUnitDelete),
    paymentStage: createDeleteMutation(handleCheckPaymentStageDelete),
    PLC: createDeleteMutation(handleCheckPLCDelete),
    level: createDeleteMutation(handleCheckLevelDelete),
    user: createDeleteMutation(handleCheckUserDelete),
    extraCharges: createDeleteMutation(handleCheckExtraChargeDelete),
    company: createDeleteMutation(handleCheckCompanyDelete),
    requirement: createDeleteMutation(handleCheckRequirementDelete),
    vendor: createDeleteMutation(handleCheckVendorDelete),
    serviceProvider: createDeleteMutation(handleCheckServiceProviderDelete),
    category: createDeleteMutation(handleCheckCategoryDelete),
    template: createDeleteMutation(handleCheckTemplateDelete),
    document: createDeleteMutation(handleCheckDocumentDelete),
    quotation: createDeleteMutation(handleCheckQuotationDelete),
    commonLiveTask: createDeleteMutation(checkLiveTaskDelete),
    lead: createDeleteMutation(handleCheckLeadDelete),
    closedDeals: createDeleteMutation(handleCheckClosedDealsDelete),
    underConsideration: createDeleteMutation(handleCheckUCDelete),
    invoice: createDeleteMutation(handleCheckInvoiceDelete),
    machine: createDeleteMutation(handleCheckMachineDelete),
    workType: createDeleteMutation(handleCheckWorkTypeDelete),
    independentPaymentStage: createDeleteMutation(handleCheckIndepdendentPaymentStageDelete)
  };

  // Extract all mutate functions
  const mutationHandlers = Object.fromEntries(
    Object.entries(deleteMutations).map(([key, mutation]) => [key, mutation.mutate])
  );

  const HandleFunctionCalls = async (id, checked) => {
    if (checked) {
      const handler = deleteMutations[type];
      if (handler) {
        try {
          const res = await handler.mutateAsync(id);
          if (res.canDelete) {
            setSelectedItems((prev) => [...prev, id]);
          } else if (!res.canDelete) {
            toast.error(`Can not delete this ${type} because there is dependencies`);
          }
        } catch (err) {
          console.error('Error checking dependencies', err);
          toast.error(
            err?.response?.data?.message || err?.message || 'Error checking dependencies'
          );
        }
      }
    } else {
      setSelectedItems((prev) => prev.filter((item) => item !== id));
    }
  };

  const handleDelete = (e) => {
    e.preventDefault();
    setCanDelete(null);
    const mergedIds = Array.from(new Set([...selectedIds, ...selectedItems]));
    onDeleteClick(mergedIds);
    setSelectedItems([]);
  };

  const handleSelectAll = async (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);

    if (checked) {
      const handler = mutationHandlers[type];
      if (!handler) return;

      setIsFetching(true); // Start loader

      try {
        const promises = arrayData.map((item) =>
          deleteMutations[type]
            .mutateAsync(item.id)
            .then((res) => {
              if (res.canDelete) return item.id;
              return null;
            })
            .catch(() => null)
        );

        const results = await Promise.all(promises);
        const validIds = results.filter((id) => id !== null);
        setSelectedItems(validIds);
      } finally {
        setIsFetching(false); // Stop loader
      }
    } else {
      setSelectedItems([]);
    }
  };

  return (
    <div className="flex flex-col justify-start items-center z-10">
      <Loader isVisible={isFetching} />
      <h1 className=" text-center text-[1.4rem] font-medium text-primary">
        Are you sure you want to delete the {title}{' '}
        {title !== 'document' && title !== 'quotation' && `'${name}'`} ?
      </h1>
      {isNotBulk && (
        <div className="flex flex-col justify-start items-center mt-4 w-[91%]  bg-[#f7f4d0] pt-[4px] pb-[4px] pl-2 pr-2 rounded-[4px]">
          <div className="flex justify-between w-[99%] items-center bg-white p-2 pt-1 pb-1">
            <p>Would you like to delete more {title} ?</p>
            <div
              onClick={() => {
                setIsBulk(!isBulk);
                setSelectedItems([]);
                setSelectAll(false);
              }}
              className="bg-amber-100 p-2 pt-1 pb-1 rounded-[5px] flex justify-center items-center gap-2 cursor-pointer">
              Bulk Delete
              <input
                type="checkbox"
                checked={isBulk}
                readOnly
                className="outline-none border-none"
              />
            </div>
          </div>
          {isBulk && (
            <div className="flex justify-end w-[100%] pr-[1.2rem] pt-3">
              <div className="flex gap-2">
                <p>Select All: </p>
                <input
                  className="outline-none border-none"
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                />
              </div>
            </div>
          )}

          {isBulk && (
            <div className="pl-[10px] w-[100%] pr-[19px] mt-2 mb-2 max-h-[19rem] overflow-auto">
              {arrayData &&
                arrayData.map((c, index) => (
                  <div key={index} className="flex justify-between items-center text-[#5b5b5b]">
                    {index + 1}.{' '}
                    {type === 'unit'
                      ? c.unitName
                      : type === 'company'
                        ? c.company_name
                        : type === 'requirement'
                          ? c.task
                          : type === 'serviceProvider'
                            ? c.user_name
                            : type === 'template'
                              ? c.document_name
                              : type === 'document'
                                ? c.customer_name
                                : type === 'quotation'
                                  ? c.vendor
                                  : type === 'workType'
                                    ? c.label
                                    : c.name}
                    <input
                      className="outline-none border-none"
                      type="checkbox"
                      checked={selectedIds.includes(c.id) || selectedItems.includes(c.id)}
                      disabled={selectedIds.includes(c.id)} // Disable if already selected
                      onChange={(e) => HandleFunctionCalls(c.id, e.target.checked)}
                    />
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
      <div className="w-full flex justify-end items-center space-x-4 mt-8">
        <Button type="reset" onClick={onClose} displayType="secondary">
          Cancel
        </Button>
        <Button
          type="button"
          onClick={(e) => {
            e.preventDefault(); // extra safety
            handleDelete(e);
          }}
          displayType="primary-red">
          Delete
        </Button>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal;
